/** Why an itinerary delete was refused (the DELETE's 409), said where the operator asked
 *  instead of in a snackbar: what depends on it, one line each, and the way out —
 *  unpublishing hides it instead. A placeholder's refusal, and an in-use refusal whose
 *  dependents were not counted, show the server's own sentence. */
import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';

import { AppDialog } from '@/components/AppDialog';

import type { ItineraryDeleteRefusal } from './deleteRefusal';

interface Props {
  /** The refusal to explain; the dialog is open while there is one. */
  refusal: ItineraryDeleteRefusal | null;
  /** The itinerary's display name. */
  name: string;
  published: boolean;
  /** The page's own unpublish; offered while an in-use itinerary is still published.
   *  Absent: the page cannot unpublish yet (the record's VIEW half) — the dialog still says
   *  that unpublishing hides it, without the button. */
  onUnpublish?: () => void;
  onClose: () => void;
}

export function ItineraryDeleteRefusalDialog({ refusal: current, name, published, onUnpublish, onClose }: Props) {
  // The last refusal stays drawn while the dialog fades out, so it never empties first.
  const [last, setLast] = useState(current);
  if (current && current !== last) setLast(current);
  const refusal = current ?? last;
  const inUse = refusal?.code === 'itinerary_in_use';
  const offerUnpublish = inUse && published && onUnpublish !== undefined;
  return (
    <AppDialog
      open={current !== null}
      onClose={onClose}
      title={`Can’t delete “${name}”`}
      actions={
        offerUnpublish ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                onClose();
                onUnpublish?.();
              }}
            >
              Unpublish
            </Button>
          </>
        ) : undefined
      }
    >
      {refusal?.dependents ? (
        <>
          <Typography variant="body2">Nothing was deleted, because these depend on it:</Typography>
          <Box component="ul" sx={{ my: 1, pl: 3 }}>
            {refusal.dependents.map((line) => (
              <Typography key={line} component="li" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {line}
              </Typography>
            ))}
          </Box>
        </>
      ) : (
        <Typography variant="body2">{refusal?.detail}</Typography>
      )}
      {inUse && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {published ? 'Unpublish it to hide it instead.' : 'It’s already unpublished, so it is hidden.'}
        </Typography>
      )}
    </AppDialog>
  );
}
